
import React, { useEffect, useState } from 'react';
import { ShieldCheck, LockKeyhole } from 'lucide-react';
import { getSuperAdminCsrf } from './superAdminApi';
import './PrivilegedPasswordManagement.css';

const BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const API = `${BASE}/api/security/2fa/`;

const emptyForm = {
  new_password: '',
  confirm_password: '',
  actor_password: '',
  actor_otp: '',
};

async function apiError(response) {
  const data = await response.json().catch(() => ({}));
  return new Error(data.detail || `Request failed (${response.status})`);
}

export default function PrivilegedPasswordManagement() {
  const [targets, setTargets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    let mounted = true;

    async function loadTargets() {
      try {
        const response = await fetch(
          `${API}recovery/password-target/`,
          { credentials: 'include', cache: 'no-store' }
        );

        if (!response.ok) throw await apiError(response);

        const data = await response.json();
        const accounts = Array.isArray(data.targets) ? data.targets : [];

        if (mounted) {
          setTargets(accounts);
          setSelectedId('');
        }
      } catch (err) {
        if (mounted) setError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadTargets();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedTarget = targets.find(
    account => String(account.user_id) === selectedId
  );

  const update = (name, value) => {
    setForm(previous => ({ ...previous, [name]: value }));
  };

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedTarget) {
      setError('Please select an authorized Django Superuser.');
      return;
    }

    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match.');
      return;
    }

    if (!window.confirm(
      `Reset ${selectedTarget.username}'s password and revoke their active sessions?`
    )) {
      return;
    }

    setBusy(true);

    try {
      const { csrfToken } = await getSuperAdminCsrf();

      const response = await fetch(`${API}recovery/password-reset/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          target_user_id: selectedTarget.user_id,
          ...form,
        }),
      });

      if (!response.ok) throw await apiError(response);

      const result = await response.json();

      setForm({ ...emptyForm });
      setSelectedId('');
      setSuccess(
        `Password updated for ${selectedTarget.username}. ` +
        `${result.sessions_revoked} session(s) revoked. ` +
        `Audit #${result.audit_id}.`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sa-panel sa-privileged-password">
      <div className="sa-privileged-heading">
        <ShieldCheck size={26} />
        <div>
          <h2>Django Superuser Password Management</h2>
          <p className="sa-muted">
            Platform Super Admin authorization required
          </p>
        </div>
      </div>

      {loading ? (
        <p>Loading authorized Django Superusers…</p>
      ) : (
        <>
          {error && (
            <p className="sa-error" role="alert">{error}</p>
          )}

          {success && (
            <p className="sa-privileged-success" role="status">
              {success}
            </p>
          )}

          {targets.length === 0 ? (
            <p className="sa-muted">
              No eligible Django Superuser accounts are configured.
            </p>
          ) : (
            <>
              <form
                className="sa-privileged-form"
                onSubmit={submit}
                autoComplete="off"
              >
                <label>
                  Select Django Superuser
                  <select
                    required
                    value={selectedId}
                    disabled={busy}
                    onChange={event => {
                      setSelectedId(event.target.value);
                      setSuccess('');
                    }}
                  >
                    <option value="">-- Select an account --</option>
                    {targets.map(account => (
                      <option
                        key={account.user_id}
                        value={String(account.user_id)}
                      >
                        {account.username}
                      </option>
                    ))}
                  </select>
                </label>

                {selectedTarget && (
                  <div className="sa-privileged-target">
                    <LockKeyhole size={20} />
                    <div>
                      <small>Selected authorized account</small>
                      <strong>{selectedTarget.username}</strong>
                      <span>Django Superuser</span>
                    </div>
                  </div>
                )}

                <p className="sa-muted">
                  This action changes the selected account's password
                  and revokes its database-backed sessions. Its
                  authenticator remains enrolled.
                </p>

                <label>
                  New password
                  <input
                    required
                    type="password"
                    minLength={12}
                    autoComplete="new-password"
                    value={form.new_password}
                    onChange={event =>
                      update('new_password', event.target.value)
                    }
                  />
                </label>

                <label>
                  Confirm new password
                  <input
                    required
                    type="password"
                    minLength={12}
                    autoComplete="new-password"
                    value={form.confirm_password}
                    onChange={event =>
                      update('confirm_password', event.target.value)
                    }
                  />
                </label>

                <label>
                  Your Platform Super Admin password
                  <input
                    required
                    type="password"
                    autoComplete="current-password"
                    value={form.actor_password}
                    onChange={event =>
                      update('actor_password', event.target.value)
                    }
                  />
                </label>

                <label>
                  Fresh authenticator OTP
                  <input
                    required
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={form.actor_otp}
                    onChange={event =>
                      update('actor_otp', event.target.value)
                    }
                  />
                </label>

                <button
                  className="sa-primary"
                  disabled={
                    busy ||
                    !selectedTarget ||
                    form.new_password !== form.confirm_password
                  }
                >
                  {busy
                    ? 'Verifying…'
                    : 'Reset Password & Revoke Sessions'}
                </button>
              </form>
            </>
          )}
        </>
      )}
    </section>
  );
}
